import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { useAuth, formatApiError } from "../store/auth";

const COUNTRIES = [
  { value: "ES", label: "España" },
  { value: "MX", label: "México" },
  { value: "CO", label: "Colombia" },
  { value: "CL", label: "Chile" },
  { value: "AR", label: "Argentina" },
  { value: "PE", label: "Perú" },
  { value: "UY", label: "Uruguay" },
  { value: "OTROS", label: "Otros" },
];

const SIZE_RANGES = [
  { value: "10", label: "1-10 empleados" },
  { value: "50", label: "11-50 empleados" },
  { value: "200", label: "51-200 empleados" },
  { value: "500", label: "201-500 empleados" },
  { value: "1000", label: "Más de 500 empleados" },
];

const PLANS = [
  { id: "starter", name: "Starter", price: "Gratis", desc: "Para empezar con ISO 27001.", enabled: true },
  { id: "pro", name: "Pro", price: "Próximamente", desc: "ENS y RGPD incluidos.", enabled: false },
  { id: "business", name: "Business", price: "Próximamente", desc: "Multi-framework.", enabled: false },
  { id: "enterprise", name: "Enterprise", price: "Próximamente", desc: "Soporte dedicado.", enabled: false },
];

export default function Register() {
  const navigate = useNavigate();
  const { register } = useAuth();

  const [step, setStep] = useState(1);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    user_email: "",
    user_name: "",
    user_password: "",
    confirm_password: "",
    org_name: "",
    country: "ES",
    sector: "",
    size: "10",
    plan: "starter",
  });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validateStep1 = () => {
    if (!form.user_email || !form.user_name) return "Completa todos los campos.";
    if (form.user_password.length < 8) return "La contraseña debe tener al menos 8 caracteres.";
    if (!/(?=.*[A-Za-z])(?=.*\d)/.test(form.user_password))
      return "La contraseña debe incluir al menos una letra y un número.";
    if (form.user_password !== form.confirm_password)
      return "Las contraseñas no coinciden.";
    return "";
  };

  const validateStep2 = () => {
    if (!form.org_name) return "Indica el nombre de la organización.";
    return "";
  };

  const next = () => {
    setError("");
    if (step === 1) {
      const err = validateStep1();
      if (err) return setError(err);
    }
    if (step === 2) {
      const err = validateStep2();
      if (err) return setError(err);
    }
    setStep((s) => Math.min(4, s + 1));
  };

  const back = () => {
    setError("");
    setStep((s) => Math.max(1, s - 1));
  };

  const submit = async () => {
    setError("");
    setSubmitting(true);
    try {
      await register({
        user_email: form.user_email,
        user_name: form.user_name,
        user_password: form.user_password,
        org_name: form.org_name,
        country: form.country,
        sector: form.sector,
        size: parseInt(form.size, 10),
        plan: form.plan,
      });
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "No se pudo crear la cuenta.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <div className="w-full max-w-2xl bg-white border border-slate-200 rounded-lg p-8 shadow-sm" data-testid="register-card">
        <div className="mb-6">
          <div className="text-[20px] font-semibold tracking-tight text-slate-900">Nimbus</div>
          <div className="text-[14px] text-slate-500 mt-1">Crea tu cuenta en 4 pasos</div>
        </div>

        <Stepper step={step} />

        {step === 1 && (
          <div className="space-y-4 mt-6" data-testid="register-step-1">
            <h2 className="text-[16px] font-semibold text-slate-900">Datos del usuario</h2>
            <div>
              <Label htmlFor="user_name">Nombre completo</Label>
              <Input id="user_name" data-testid="reg-name" value={form.user_name} onChange={(e) => update("user_name", e.target.value)} />
            </div>
            <div>
              <Label htmlFor="user_email">Correo electrónico</Label>
              <Input id="user_email" data-testid="reg-email" type="email" value={form.user_email} onChange={(e) => update("user_email", e.target.value)} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="user_password">Contraseña</Label>
                <Input id="user_password" data-testid="reg-password" type="password" value={form.user_password} onChange={(e) => update("user_password", e.target.value)} />
              </div>
              <div>
                <Label htmlFor="confirm_password">Confirmar contraseña</Label>
                <Input id="confirm_password" data-testid="reg-confirm" type="password" value={form.confirm_password} onChange={(e) => update("confirm_password", e.target.value)} />
              </div>
            </div>
            <p className="text-[12px] text-slate-500">Mínimo 8 caracteres, con al menos una letra y un número.</p>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4 mt-6" data-testid="register-step-2">
            <h2 className="text-[16px] font-semibold text-slate-900">Datos de la organización</h2>
            <div>
              <Label htmlFor="org_name">Nombre</Label>
              <Input id="org_name" data-testid="reg-org-name" value={form.org_name} onChange={(e) => update("org_name", e.target.value)} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>País</Label>
                <Select value={form.country} onValueChange={(v) => update("country", v)}>
                  <SelectTrigger data-testid="reg-country">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {COUNTRIES.map((c) => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Tamaño</Label>
                <Select value={form.size} onValueChange={(v) => update("size", v)}>
                  <SelectTrigger data-testid="reg-size">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SIZE_RANGES.map((s) => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label htmlFor="sector">Sector</Label>
              <Input id="sector" data-testid="reg-sector" value={form.sector} onChange={(e) => update("sector", e.target.value)} placeholder="Ej. Tecnología, Salud, Finanzas..." />
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4 mt-6" data-testid="register-step-3">
            <h2 className="text-[16px] font-semibold text-slate-900">Selecciona tu plan</h2>
            <div className="grid grid-cols-2 gap-3">
              {PLANS.map((p) => {
                const selected = form.plan === p.id;
                return (
                  <button
                    type="button"
                    key={p.id}
                    data-testid={`reg-plan-${p.id}`}
                    disabled={!p.enabled}
                    onClick={() => p.enabled && update("plan", p.id)}
                    className={`text-left border rounded-md p-4 ${
                      selected ? "border-primary ring-2 ring-primary/20" : "border-slate-200"
                    } ${!p.enabled ? "opacity-50 cursor-not-allowed bg-slate-50" : "hover:border-slate-300 bg-white"}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-slate-900">{p.name}</span>
                      <span className="text-[12px] text-slate-500">{p.price}</span>
                    </div>
                    <p className="text-[12px] text-slate-500 mt-2">{p.desc}</p>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-4 mt-6" data-testid="register-step-4">
            <h2 className="text-[16px] font-semibold text-slate-900">Confirmación</h2>
            <div className="border border-slate-200 rounded-md p-4 text-[14px] space-y-2 bg-slate-50">
              <Row label="Nombre">{form.user_name}</Row>
              <Row label="Correo">{form.user_email}</Row>
              <Row label="Organización">{form.org_name}</Row>
              <Row label="País">{COUNTRIES.find((c) => c.value === form.country)?.label}</Row>
              <Row label="Tamaño">{SIZE_RANGES.find((s) => s.value === form.size)?.label}</Row>
              <Row label="Sector">{form.sector || "—"}</Row>
              <Row label="Plan">Starter (Gratis)</Row>
            </div>
          </div>
        )}

        {error && (
          <div data-testid="register-error" className="text-[12px] text-destructive mt-4">
            {error}
          </div>
        )}

        <div className="flex items-center justify-between mt-6 pt-4 border-t border-slate-200">
          <Button variant="ghost" type="button" onClick={back} disabled={step === 1} data-testid="reg-back">
            <ChevronLeft className="h-4 w-4 mr-1" /> Atrás
          </Button>
          {step < 4 ? (
            <Button type="button" onClick={next} data-testid="reg-next">
              Siguiente <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          ) : (
            <Button type="button" onClick={submit} disabled={submitting} data-testid="reg-submit">
              {submitting ? "Creando..." : "Crear cuenta"}
            </Button>
          )}
        </div>

        <div className="mt-6 text-center text-[14px] text-slate-600">
          ¿Ya tienes cuenta?{" "}
          <Link to="/login" className="text-primary font-medium hover:underline" data-testid="register-to-login">
            Inicia sesión
          </Link>
        </div>
      </div>
    </div>
  );
}

function Stepper({ step }) {
  const labels = ["Usuario", "Organización", "Plan", "Confirmar"];
  return (
    <div className="flex items-center gap-2">
      {labels.map((l, i) => {
        const idx = i + 1;
        const active = idx === step;
        const done = idx < step;
        return (
          <div key={l} className="flex items-center gap-2 flex-1">
            <div
              className={`h-7 w-7 rounded-full flex items-center justify-center text-[12px] font-semibold ${
                done
                  ? "bg-primary text-white"
                  : active
                  ? "bg-primary/10 text-primary border border-primary"
                  : "bg-slate-100 text-slate-400"
              }`}
            >
              {done ? <Check className="h-3 w-3" /> : idx}
            </div>
            <span className={`text-[12px] ${active ? "text-slate-900" : "text-slate-500"}`}>{l}</span>
            {idx < labels.length && <div className="flex-1 h-px bg-slate-200 mx-2" />}
          </div>
        );
      })}
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-slate-500">{label}</span>
      <span className="text-slate-900 font-medium">{children}</span>
    </div>
  );
}
